export function hexTrio(hex) {
  // https://stackoverflow.com/a/5624139/2655356

  let result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);

  if (result) {
    return [result[1], result[2], result[3]];
  } else {
    return ['00', 'f8', 'bf'];
  }
}

export function hexToRgb(hex) {
  let rgbObject = _hexToRgbObject(hex);

  return _rgbObjectToRgb(rgbObject);
}

export function componentToHex(c) {
  let hex = c.toString(16);
  return hex.length === 1 ? `0${hex}` : hex;
}

export function rgbToHex(r, g, b) {
  return `#${componentToHex(r)}${componentToHex(g)}${componentToHex(b)}`;
}

export function hexCodeIsLighterThan(hex, comparisonHex) {
  let rgbObject = _hexToRgbObject(hex);
  let comparisonRgbObject = _hexToRgbObject(comparisonHex);

  if (!rgbObject || !comparisonRgbObject) {
    return;
  }

  return _getContrastFromRgbObject(rgbObject) > _getContrastFromRgbObject(comparisonRgbObject);
}

export function hexCodeIsLight(hex) {
  let rgbObject = _hexToRgbObject(hex);

  // 125 - magic number, seemingly equal to $cio-green lightened by 5 steps (lighten() Sass helper)
  // (no description in original commit / clear motivation behind it - adding code comment to reference my research)
  return rgbObject && _getContrastFromRgbObject(rgbObject) > 125;
}

export function lightenOrDarkenColor(hex, amount = 0, output = 'rgb') {
  let hslObject = _hexToHslObject(hex);
  let newHslObject = { h: hslObject.h, s: hslObject.s, l: hslObject.l + amount / 100 };

  let newRgbObject = _hslToRgbObject(newHslObject);

  if (output === 'hex') {
    let { r, g, b } = newRgbObject;
    return rgbToHex(r, g, b);
  }

  return _rgbObjectToRgb(newRgbObject);
}

function _rgbObjectToRgb(rgbObject) {
  return `rgb(${rgbObject.r}, ${rgbObject.g}, ${rgbObject.b})`;
}

function _hexToRgbObject(hex) {
  let trio = hexTrio(hex);

  return {
    r: parseInt(trio[0], 16),
    g: parseInt(trio[1], 16),
    b: parseInt(trio[2], 16),
  };
}

// The below color conversion algorithms are from https://gist.github.com/ststeiger/3f06add6dba729334745d07a02994063
function _hexToHslObject(hex) {
  let { r, g, b } = _hexToRgbObject(hex);

  r /= 255;
  g /= 255;
  b /= 255;

  let max = Math.max(r, g, b);
  let min = Math.min(r, g, b);
  let average = (max + min) / 2;

  let h = average;
  let s = average;
  let l = average;

  if (max === min) {
    h = s = 0; // achromatic
  } else {
    let d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);

    switch (max) {
      case r:
        h = (g - b) / d + (g < b ? 6 : 0);
        break;
      case g:
        h = (b - r) / d + 2;
        break;
      case b:
        h = (r - g) / d + 4;
        break;
    }

    h /= 6;
  }

  return { h, s, l };
}

function _hslToRgbObject({ h, s, l }) {
  let r;
  let g;
  let b;

  if (s === 0) {
    r = g = b = l; // achromatic
  } else {
    let q = l < 0.5 ? l * (1 + s) : l + s - l * s;
    let p = 2 * l - q;

    r = _hueToRgb(p, q, h + 1 / 3);
    g = _hueToRgb(p, q, h);
    b = _hueToRgb(p, q, h - 1 / 3);
  }

  return {
    r: Math.max(0, Math.min(Math.round(r * 255), 255)),
    g: Math.max(0, Math.min(Math.round(g * 255), 255)),
    b: Math.max(0, Math.min(Math.round(b * 255), 255)),
  };
}

function _hueToRgb(p, q, t) {
  if (t < 0) {
    t += 1;
  }

  if (t > 1) {
    t -= 1;
  }

  if (t < 1 / 6) {
    return p + (q - p) * 6 * t;
  }

  if (t < 1 / 2) {
    return q;
  }

  if (t < 2 / 3) {
    return p + (q - p) * (2 / 3 - t) * 6;
  }

  return p;
}

// https://stackoverflow.com/a/11868159/2655356
function _getContrastFromRgbObject(rgbObject) {
  return Math.round((parseInt(rgbObject.r) * 299 + parseInt(rgbObject.g) * 587 + parseInt(rgbObject.b) * 114) / 1000);
}
